import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// Components
import ConnectionsMobileSubnav from './ConnectionsMobileSubnav';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import SectionHeader from '../../../_molecules/SectionHeader';
import ErrorHandler from '../../../_molecules/ErrorHandler';
import TrackPageView from '@tracking/TrackPageView';
import SearchCompaniesSaved from '../../../searches/SearchCompaniesSaved';
import EmptyStateContainer from '@molecules/EmptyStateContainer';
import LoadingArea from '@molecules/LoadingArea';
import { BodyLarge, DivWithShadowAlternate, H3, LinkSmall, SectionWrapperInner, SectionWrapperOuter } from '@styled';

// Functionality
import useCompaniesSavedActive from '@hooks/useCompaniesSavedActive';
import useAlgoliaCompanies from '@hooks/search/useAlgoliaCompanies';
import useDocumentTitle from '@hooks/useDocumentTitle';

const SearchContainerStyled = styled(DivWithShadowAlternate)`
    margin-top: 40px;
    padding: 32px;
`;

const CompaniesSavedPage = () => {
    const [savedCompanyIds, setSavedCompanyIds] = useState();
    const { data: savedCompanies } = useCompaniesSavedActive();
    useDocumentTitle('Saved Companies');

    // This is necessary because saved records are not always in sync with what's in Algolia,
    // leading to an empty Algolia search instead of the Empty State Container we want to show.
    const { companies: algoliaCompanies } = useAlgoliaCompanies(savedCompanyIds);

    useEffect(() => {
        if (!savedCompanies) {
            return;
        }

        setSavedCompanyIds(savedCompanies.map(c => c.recordId));
    }, [savedCompanies]);

    if (!algoliaCompanies) {
        return <LoadingArea />;
    }

    return (
        <ErrorHandler>
            <TrackPageView>
                <ConnectionsMobileSubnav />

                <SectionWrapperOuter>
                    <SectionWrapperInner>
                        <div className="fade-in-fast pb-4">
                            <SectionHeader title="My ICSC Connections" />

                            <div className="flex justify-content-between mb-1.5rem">
                                <SectionBreadcrumbs className="mt-2">
                                    <SectionBreadcrumb route="/my-icsc/connections">Connections</SectionBreadcrumb>
                                    <SectionBreadcrumb isActive>Saved Companies</SectionBreadcrumb>
                                </SectionBreadcrumbs>
                            </div>

                            <H3 className="mt-2 mb-3">Saved Companies</H3>

                            <BodyLarge>These are the ICSC companies that you’ve saved.</BodyLarge>

                            <div className="mt-4">
                                {algoliaCompanies.length === 0 && (
                                    <EmptyStateContainer
                                        Body={() => (
                                            <>
                                                Click on the star on any company card to save that company as a
                                                favorite.
                                            </>
                                        )}
                                        header="You haven't saved any companies yet."
                                        imageSrc="/assets/modules/images/SavedContactsEmptyStatePlaceholder.jpg"
                                        imageAlt="No saved companies"
                                        ctaText="View All Companies"
                                        ctaHref="/search?type=companies"
                                        onCTAClick={() => {}}
                                    />
                                )}

                                {algoliaCompanies.length > 0 && (
                                    <SearchContainerStyled>
                                        <SearchCompaniesSaved
                                            additionalFilters="status:Active"
                                            defaultViewType="list"
                                            parentView="Connections"
                                            showPagingToolbar
                                            showSearchBox
                                            showSearchOptions
                                            updateSearchStateUrl
                                        />
                                    </SearchContainerStyled>
                                )}
                            </div>
                        </div>
                    </SectionWrapperInner>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};

export default CompaniesSavedPage;
